import React from 'react';
import { loadStripe, Stripe, StripeElementLocale } from '@stripe/stripe-js';
import { Elements } from '@stripe/react-stripe-js';
import { usePaymentsContext } from '../../shared/providers/PaymentsProvider';
import { ERROR_MESSAGES } from '../../shared/constants';

interface StripeProviderProps {
  children: React.ReactNode;
  options?: {
    fonts?: Array<{
      cssSrc: string;
    }>;
    locale?: string;
    apiVersion?: string;
  };
}

let stripePromise: Promise<Stripe | null> | null = null;

const getStripe = (publishableKey: string): Promise<Stripe | null> => {
  if (!stripePromise) {
    stripePromise = loadStripe(publishableKey);
  }
  return stripePromise;
};

export const StripeProvider: React.FC<StripeProviderProps> = ({
  children,
  options = {},
}) => {
  const { config, initialized } = usePaymentsContext();

  const stripe = React.useMemo(() => {
    if (!initialized || !config?.publishableKey) {
      if (initialized && !config) {
        console.error(ERROR_MESSAGES.PROVIDER_NOT_CONFIGURED);
      }
      return null;
    }
    return getStripe(config.publishableKey);
  }, [config, initialized]);

  if (!initialized || !config) {
    if (config?.environment === 'development') {
      return (
        <div
          style={{
            padding: '20px',
            backgroundColor: '#fee',
            border: '1px solid #fcc',
            borderRadius: '4px',
            margin: '10px',
            fontFamily: 'monospace',
          }}
        >
          <strong>Payments Provider Error:</strong>
          <br />
          {ERROR_MESSAGES.PROVIDER_NOT_CONFIGURED}
          <br />
          Please check your PaymentsProvider configuration.
        </div>
      );
    }
    return null;
  }

  const elementsOptions = {
    fonts: options.fonts,
    locale: options.locale as StripeElementLocale,
    appearance: {
      theme: 'stripe' as const,
    },
  };

  return (
    <Elements stripe={stripe} options={elementsOptions}>
      {children}
    </Elements>
  );
};

export default StripeProvider;
